import { Fragment } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

export interface Step {
  id: string | number
  label: string
  status: 'completed' | 'current' | 'pending'
}

interface StepNavigationProps {
  steps: Step[]
  onStepClick?: (id: Step['id']) => void
  className?: string
}

export function StepNavigation({ steps, onStepClick, className }: StepNavigationProps) {
  return (
    <nav
      className={cn(
        'flex w-full flex-row items-center justify-between border-b border-line bg-surface px-4 py-4',
        className,
      )}
      aria-label="Pasos del asistente"
    >
      {steps.map((step, index) => {
        const isClickable = step.status === 'completed' && onStepClick

        return (
          <Fragment key={step.id}>
            <button
              type="button"
              onClick={isClickable ? () => onStepClick(step.id) : undefined}
              disabled={!isClickable}
              className={cn(
                'flex flex-col items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 rounded-md',
                isClickable ? 'cursor-pointer' : 'cursor-default',
              )}
              aria-current={step.status === 'current' ? 'step' : undefined}
            >
              <span
                className={cn('flex h-9 w-9 items-center justify-center rounded-full', {
                  'bg-ink text-white': step.status === 'completed' || step.status === 'current',
                  'border border-line bg-surface text-ink-muted opacity-60':
                    step.status === 'pending',
                })}
              >
                {step.status === 'completed' ? (
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                ) : (
                  <span className="text-sm font-semibold">{index + 1}</span>
                )}
              </span>

              <span
                className={cn(
                  'whitespace-nowrap text-center text-sm font-medium tracking-tight',
                  {
                    'text-ink': step.status === 'completed' || step.status === 'current',
                    'text-ink-muted': step.status === 'pending',
                  },
                )}
              >
                {step.label}
              </span>
            </button>

            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={cn('mx-4 h-px flex-1 rounded-sm', {
                  'bg-ink': step.status === 'completed',
                  'bg-line': step.status !== 'completed',
                })}
              />
            )}
          </Fragment>
        )
      })}
    </nav>
  )
}
